import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiFileText, FiUpload, FiDownload, FiTrash2, FiEdit2, FiEye, FiX,
  FiCheck, FiCalendar, FiDollarSign, FiUser, FiMail, FiTag, FiPlus,
  FiSearch, FiFilter, FiLoader, FiAlertCircle, FiCheckCircle, FiClock,
  FiArchive, FiFile, FiPaperclip
} = FiIcons;

const CONTRACT_TYPES = [
  { value: 'contract', label: 'Contract', icon: FiFileText },
  { value: 'nda', label: 'NDA', icon: FiFile },
  { value: 'investment', label: 'Investment Agreement', icon: FiDollarSign },
  { value: 'partnership', label: 'Partnership', icon: FiUser },
  { value: 'sla', label: 'Service Level Agreement', icon: FiCheckCircle },
  { value: 'mou', label: 'Memorandum of Understanding', icon: FiFileText },
  { value: 'term_sheet', label: 'Term Sheet', icon: FiFileText },
  { value: 'other', label: 'Other Document', icon: FiArchive }
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-500', textColor: 'text-slate-300' },
  { value: 'pending', label: 'Pending Signature', color: 'bg-amber-500', textColor: 'text-amber-300' },
  { value: 'signed', label: 'Signed', color: 'bg-emerald-500', textColor: 'text-emerald-300' },
  { value: 'active', label: 'Active', color: 'bg-cyan-500', textColor: 'text-cyan-300' },
  { value: 'expired', label: 'Expired', color: 'bg-red-500', textColor: 'text-red-300' },
  { value: 'terminated', label: 'Terminated', color: 'bg-red-600', textColor: 'text-red-400' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-600', textColor: 'text-gray-400' }
];

const ContractVault = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    document_type: 'contract',
    description: '',
    status: 'draft',
    signatory_name: '',
    signatory_email: '',
    contract_value: '',
    currency: 'USD',
    effective_date: '',
    expiration_date: '',
    tags: '',
    notes: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contract_vault')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContracts(data || []);
    } catch (err) {
      console.error('Error fetching contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadFile = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `contracts/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('contracts')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('contracts')
      .getPublicUrl(filePath);

    return { url: publicUrl, fileName: file.name, size: file.size };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      let fileData = {};
      if (selectedFile) {
        fileData = await uploadFile(selectedFile);
      }

      const contractData = {
        title: formData.title,
        document_type: formData.document_type,
        description: formData.description || null,
        status: formData.status,
        signatory_name: formData.signatory_name || null,
        signatory_email: formData.signatory_email || null,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
        currency: formData.currency,
        effective_date: formData.effective_date || null,
        expiration_date: formData.expiration_date || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        notes: formData.notes || null,
        file_url: fileData.url || null,
        file_name: fileData.fileName || null,
        file_size: fileData.size || null,
        created_by: user.id,
        signed_at: formData.status === 'signed' ? new Date().toISOString() : null
      };

      if (editMode && selectedContract) {
        const { error } = await supabase
          .from('contract_vault')
          .update(contractData)
          .eq('id', selectedContract.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('contract_vault')
          .insert([contractData]);

        if (error) throw error;
      }

      setShowAddModal(false);
      setEditMode(false);
      setSelectedContract(null);
      resetForm();
      fetchContracts();
    } catch (err) {
      console.error('Error saving contract:', err);
      alert('Error saving contract: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this contract?')) return;

    try {
      const { error } = await supabase
        .from('contract_vault')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchContracts();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Error deleting contract:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      document_type: 'contract',
      description: '',
      status: 'draft',
      signatory_name: '',
      signatory_email: '',
      contract_value: '',
      currency: 'USD',
      effective_date: '',
      expiration_date: '',
      tags: '',
      notes: ''
    });
    setSelectedFile(null);
  };

  const openEditModal = (contract) => {
    setFormData({
      title: contract.title,
      document_type: contract.document_type,
      description: contract.description || '',
      status: contract.status,
      signatory_name: contract.signatory_name || '',
      signatory_email: contract.signatory_email || '',
      contract_value: contract.contract_value?.toString() || '',
      currency: contract.currency || 'USD',
      effective_date: contract.effective_date || '',
      expiration_date: contract.expiration_date || '',
      tags: contract.tags?.join(', ') || '',
      notes: contract.notes || ''
    });
    setSelectedContract(contract);
    setEditMode(true);
    setShowDetailModal(false);
    setShowAddModal(true);
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.signatory_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || contract.document_type === filterType;
    const matchesStatus = filterStatus === 'all' || contract.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}/20 ${statusConfig.textColor} border border-current/20`}>
        {statusConfig.label}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const typeConfig = CONTRACT_TYPES.find(t => t.value === type);
    return typeConfig?.icon || FiFileText;
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Stats
  const totalValue = contracts.reduce((sum, c) => sum + (c.contract_value || 0), 0);
  const signedCount = contracts.filter(c => c.status === 'signed' || c.status === 'active').length;
  const pendingCount = contracts.filter(c => c.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-xl p-4 rounded-xl border border-purple-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">Total Contracts</p>
              <p className="text-2xl font-bold text-white">{contracts.length}</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-500/20">
              <SafeIcon icon={FiFileText} className="text-purple-400" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-xl p-4 rounded-xl border border-cyan-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">Total Value</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalValue)}</p>
            </div>
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <SafeIcon icon={FiDollarSign} className="text-cyan-400" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-xl p-4 rounded-xl border border-emerald-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">Signed/Active</p>
              <p className="text-2xl font-bold text-white">{signedCount}</p>
            </div>
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <SafeIcon icon={FiCheckCircle} className="text-emerald-400" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-xl p-4 rounded-xl border border-amber-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">Pending</p>
              <p className="text-2xl font-bold text-white">{pendingCount}</p>
            </div>
            <div className="p-2 rounded-lg bg-amber-500/20">
              <SafeIcon icon={FiClock} className="text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-2 pl-10 pr-4 text-white placeholder-slate-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-slate-800/50 border border-slate-700 rounded-xl py-2 px-4 text-white focus:border-purple-500"
          >
            <option value="all">All Types</option>
            {CONTRACT_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-800/50 border border-slate-700 rounded-xl py-2 px-4 text-white focus:border-purple-500"
          >
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { resetForm(); setEditMode(false); setShowAddModal(true); }}
          className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-medium py-2 px-4 rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-purple-500/20"
        >
          <SafeIcon icon={FiPlus} />
          Add Contract
        </button>
      </div>

      {/* Contract List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <SafeIcon icon={FiLoader} className="animate-spin text-purple-400 text-3xl" />
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-2xl border border-slate-700/50">
          <SafeIcon icon={FiArchive} className="text-slate-500 text-5xl mx-auto mb-4" />
          <p className="text-slate-400">No contracts found</p>
          <p className="text-slate-500 text-sm mt-1">Add your first contract to get started</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContracts.map(contract => (
            <motion.div
              key={contract.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.005 }}
              onClick={() => { setSelectedContract(contract); setShowDetailModal(true); }}
              className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 backdrop-blur-xl p-5 rounded-xl border border-slate-700/50 hover:border-purple-500/30 cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
                    <SafeIcon icon={getTypeIcon(contract.document_type)} className="text-cyan-400 text-xl" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-white">{contract.title}</h3>
                      {getStatusBadge(contract.status)}
                    </div>
                    <p className="text-slate-400 text-sm line-clamp-1">{contract.description || 'No description'}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                      {contract.signatory_name && (
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiUser} className="text-slate-600" />
                          {contract.signatory_name}
                        </span>
                      )}
                      {contract.contract_value && (
                        <span className="flex items-center gap-1 text-cyan-400">
                          <SafeIcon icon={FiDollarSign} className="text-cyan-500" />
                          {formatCurrency(contract.contract_value, contract.currency)}
                        </span>
                      )}
                      {contract.effective_date && (
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiCalendar} />
                          {formatDate(contract.effective_date)}
                        </span>
                      )}
                      {contract.file_name && (
                        <span className="flex items-center gap-1 text-purple-400">
                          <SafeIcon icon={FiPaperclip} />
                          Attached
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModal(contract); }}
                    className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 text-slate-400 hover:text-white transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(contract.id); }}
                    className="p-2 rounded-lg bg-slate-700/50 hover:bg-red-600/20 text-slate-400 hover:text-red-400 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <SafeIcon icon={editMode ? FiEdit2 : FiPlus} className="text-cyan-400" />
                    {editMode ? 'Edit Contract' : 'Add New Contract'}
                  </h2>
                  <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Contract Title *</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="e.g., Series A Investment Agreement"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Document Type</label>
                    <select
                      value={formData.document_type}
                      onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                    >
                      {CONTRACT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="Brief description of the contract..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Signatory Name</label>
                    <input
                      type="text"
                      value={formData.signatory_name}
                      onChange={(e) => setFormData({ ...formData, signatory_name: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Signatory Email</label>
                    <input
                      type="email"
                      value={formData.signatory_email}
                      onChange={(e) => setFormData({ ...formData, signatory_email: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Contract Value</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={formData.contract_value}
                        onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                        className="flex-1 bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                        placeholder="100000"
                      />
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-3 text-white focus:border-purple-500"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CHF">CHF</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Effective Date</label>
                    <input
                      type="date"
                      value={formData.effective_date}
                      onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Expiration Date</label>
                    <input
                      type="date"
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="investor, series-a, equity"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Attach Document</label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-600 hover:border-purple-500 rounded-xl p-6 text-center cursor-pointer transition-colors"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      />
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-2 text-cyan-400">
                          <SafeIcon icon={FiCheck} />
                          <span>{selectedFile.name}</span>
                        </div>
                      ) : (
                        <div className="text-slate-400">
                          <SafeIcon icon={FiUpload} className="text-2xl mx-auto mb-2" />
                          <p>Click to upload PDF, DOC, or image</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Internal Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2 px-4 text-white focus:border-purple-500"
                      placeholder="Internal notes (not shared with signatories)..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-medium py-2 px-6 rounded-xl flex items-center gap-2 transition-all disabled:opacity-50"
                  >
                    {uploading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiCheck} />
                        {editMode ? 'Update Contract' : 'Save Contract'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedContract && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
                      <SafeIcon icon={getTypeIcon(selectedContract.document_type)} className="text-cyan-400 text-xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedContract.title}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(selectedContract.status)}
                        <span className="text-slate-500 text-sm">
                          {CONTRACT_TYPES.find(t => t.value === selectedContract.document_type)?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-white">
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {selectedContract.description && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-2">Description</h3>
                    <p className="text-white">{selectedContract.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <SafeIcon icon={FiUser} />
                      Signatory
                    </div>
                    <p className="text-white font-medium">{selectedContract.signatory_name || '—'}</p>
                    <p className="text-slate-400 text-sm">{selectedContract.signatory_email || '—'}</p>
                  </div>
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <SafeIcon icon={FiDollarSign} />
                      Contract Value
                    </div>
                    <p className="text-cyan-400 text-xl font-bold">
                      {formatCurrency(selectedContract.contract_value, selectedContract.currency)}
                    </p>
                  </div>
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <SafeIcon icon={FiCalendar} />
                      Effective Date
                    </div>
                    <p className="text-white">{formatDate(selectedContract.effective_date)}</p>
                  </div>
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <SafeIcon icon={FiCalendar} />
                      Expiration Date
                    </div>
                    <p className="text-white">{formatDate(selectedContract.expiration_date)}</p>
                  </div>
                </div>

                {selectedContract.tags?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedContract.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedContract.file_url && (
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiPaperclip} className="text-purple-400" />
                        <div>
                          <p className="text-white font-medium">{selectedContract.file_name}</p>
                          <p className="text-slate-500 text-sm">
                            {selectedContract.file_size ? `${(selectedContract.file_size / 1024).toFixed(1)} KB` : 'Attached document'}
                          </p>
                        </div>
                      </div>
                      <a
                        href={selectedContract.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-4 py-2 rounded-xl hover:from-purple-500 hover:to-cyan-500 transition-all"
                      >
                        <SafeIcon icon={FiDownload} />
                        Download
                      </a>
                    </div>
                  </div>
                )}

                {selectedContract.notes && (
                  <div className="bg-slate-700/30 p-4 rounded-xl">
                    <h3 className="text-sm font-medium text-slate-400 mb-2">Internal Notes</h3>
                    <p className="text-slate-300 text-sm">{selectedContract.notes}</p>
                  </div>
                )}

                <div className="text-xs text-slate-500 flex justify-between">
                  <span>Created: {formatDate(selectedContract.created_at)}</span>
                  {selectedContract.signed_at && (
                    <span>Signed: {formatDate(selectedContract.signed_at)}</span>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => handleDelete(selectedContract.id)}
                  className="px-4 py-2 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                >
                  <SafeIcon icon={FiTrash2} />
                  Delete
                </button>
                <button
                  onClick={() => openEditModal(selectedContract)}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-medium py-2 px-4 rounded-xl flex items-center gap-2 transition-all"
                >
                  <SafeIcon icon={FiEdit2} />
                  Edit Contract
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractVault;